import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { base64ToHex, hasWriteAccess } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import DropdownField from "../../components/Editing/DropdownField";
import TextField from "../../components/Editing/TextField";
import CheckboxField from "../../components/Editing/CheckboxField";
import {
  getParameterCategoryOptions,
  visibilityOptions,
  decodeValueDataFormat,
  getDuplicateLiftParameterBindings,
  getDuplicateDriveParameterBindings,
  getDuplicateDoorParameterBindings,
  getDuplicateEscalatorParameterBindings,
  getDomainOptionsByDomainGroup,
  getCategoryPathForParameter,
  escalatorParameterFields,
  doorParameterFields,
  getcommonEnumOptions,
  getGroupNameByDomainId,
  validatePreferredValues,
  validatePreferredValueDigitsOnly,
  PREFERRED_TYPE_ENUM_VALUES
} from "../../utils/pdApiUtils";

import {
  parameterFields,
  groupParameterFields,
  liftParameterFields,
  driveParameterFields,
  safetyDatatypeOptions
} from "../../utils/pdApiUtils";
import { Divider, Modal } from "semantic-ui-react";
import Permissions from "../../components/Permissions/Permissions";
import LiftParameterBindings from "../../components/LiftParameterBindings/LiftParameterBindings";
import DoorParameterBindings from "../../components/DoorParameterBindings/DoorParameterBindings";
import LiftParameterValues from "../../components/LiftParameterValues/LiftParameterValues";
import EditorDefines from "../../components/EditorDefines/EditorDefines";
import ValueDataFormat from "../../components/ValueDataFormat/ValueDataFormat";
import PreferrerValues from "../../components/PreferredValues/PreferredValues";
import DriveParameterBindings from "../../components/DriveParameterBindings/DriveParameterBindings";
import EscalatorParameterBindings from "../../components/EscalatorParameterBindings/EscalatorParameterBindings";
import { editActions } from "../../actions/editActions";
import ButtonField from "../../components/Editing/ButtonField";
import { EditLocalizationStatus } from "./EditLocalizationStatus";
import TextIndicatorField from "../../components/Editing/TextIndicatorField";
import { Form } from "semantic-ui-react";

type StateProps = {
  editedParameter: Api.AnyParameter;
  storedParameter: Api.AnyParameter;
  pdApi: State.PdApi;
  activeUser?: Api.User;
  isLoadingParameters: boolean;
  isLoadingEscalatorParameters: boolean;
  columns: State.ColumnMap;
  isShortenTextEnabled: boolean;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
  groupName: Api.GroupName;
};

/**
 * EditParameter props
 */
export type EditParameterProps = StateProps & DispatchProps & OwnProps;

export type EditParameterState = {
  isEditLocalizationStatusOpen: boolean;
  locsOriginal: Api.LocsParameter[];
  locsEdited: Api.LocsParameter[];
  userPermissions: boolean;
  dataFormatSpecialValue: number;
  floatValue?: number;
  bitMask: string;
  bitMaskValid: boolean;
};

/**
 * EditParameter component
 */
export class EditParameter extends React.Component<EditParameterProps, EditParameterState> {
  constructor(props: EditParameterProps) {
    super(props);
    this.state = {
      isEditLocalizationStatusOpen: false,
      locsOriginal: [],
      locsEdited: [],
      userPermissions: hasWriteAccess(props.activeUser, props.groupName),
      dataFormatSpecialValue: 128,
      floatValue: 0,
      bitMask: "AA==",
      bitMaskValid: true
    };
  }

  // Cached validation to avoid re-running on every render when inputs haven't changed.
  // With 150–250 preferred values, this prevents unnecessary iteration on each re-render.
  private cachedValidation: {
    preferredValues: number[];
    typeEnumValue: number;
    valueDataFormat: number;
    isEscalator: boolean;
    result: { isValid: boolean; errorMessage: string };
  } | null = null;

  private getValidationError = (
    preferredValues: number[],
    typeEnumValue: number,
    valueDataFormat: number,
    isEscalator: boolean
  ): string | undefined => {
    const cached = this.cachedValidation;
    if (
      cached &&
      cached.preferredValues === preferredValues &&
      cached.typeEnumValue === typeEnumValue &&
      cached.valueDataFormat === valueDataFormat &&
      cached.isEscalator === isEscalator
    ) {
      return cached.result.errorMessage || undefined;
    }

    const result = validatePreferredValues(preferredValues, typeEnumValue, valueDataFormat, isEscalator);
    this.cachedValidation = { preferredValues, typeEnumValue, valueDataFormat, isEscalator, result };
    return result.errorMessage || undefined;
  };
  private emptyJsxElement = (<></>);

  public render(): JSX.Element {
    const { isEditLocalizationStatusOpen } = this.state;
    const {
      editedParameter,
      storedParameter,
      activeUser,
      toggleModal,
      groupName,
      columns,
      pdApi,
      isLoadingParameters,
      isLoadingEscalatorParameters,
      isShortenTextEnabled
    } = this.props;
    const isNew = !storedParameter;

    const parametersType = (groupName.toLowerCase() + "Parameters") as Api.OptionsGrud;
    const specificProperties = this.specificProperties(groupName);
    const hasSpecificProperties = specificProperties !== this.emptyJsxElement;
    return (
      <Editor
        itemName="parameter"
        originalObject={storedParameter}
        editedObject={editedParameter}
        hasWritePermission={hasWriteAccess(activeUser, groupName)}
        isValid={
          this.isAllFieldsValid() &&
          (!isLoadingParameters || !isLoadingEscalatorParameters)
        }
        cancel={this.props.editActions.cancelEditing}
        update={() => {
          if (this.isSafetyParameter(editedParameter) && editedParameter.bitMask) {
            editedParameter.bitMask = this.state.bitMask;
          }
          this.props.editActions.setEdited({
            ...editedParameter,
            locs: this.state.locsEdited
          });
          this.props.editActions.update(parametersType);
        }}
        create={() => {
          if (this.isSafetyParameter(editedParameter) && editedParameter.bitMask) {
            editedParameter.bitMask = this.state.bitMask;
          }
          this.props.editActions.setEdited({
            ...editedParameter,
            locs: this.state.locsEdited
          });
          this.props.editActions.add(parametersType);
        }}
        copy={
          groupName === "Group"
            ? undefined
            : "liftParameterBindings" in editedParameter
            ? () =>
                this.props.editActions.startEditing(
                  { ...editedParameter, liftParameterBindings: [], liftParameterValues: [] },
                  true
                )
            : "escalatorParameterBindings" in editedParameter
            ? () =>
                this.props.editActions.startEditing(
                  { ...editedParameter, escalatorParameterBindings: []},
                  true
                )
            : "doorParameterBindings" in editedParameter
            ? () =>
                this.props.editActions.startEditing(
                  { ...editedParameter, doorParameterBindings: [] },
                  true
                )
            : () => this.props.editActions.startEditing(editedParameter, true)
        }
        remove={{ callback: toggleModal }}
      >
        {this.commonProperties(isNew, groupName)}
        {hasSpecificProperties && (
          <div>
            <Divider horizontal={true}>{`${groupName} properties`}</Divider>
            {specificProperties}
          </div>
        )}
        <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
          <EditLocalizationStatus
            itemType={"Parameter"}
            pdApi={pdApi}
            editedItem={{ ...editedParameter, locs: this.state.locsEdited }}
            locsOriginal={this.state.locsOriginal || []}
            getOption="parameterLocalizations"
            columns={columns["Parameter Localization Status"]}
            toggleEditLocalizationStatusModal={this.toggleEditLocalizationStatusModal}
            setLocalizationsEdited={this.setLocalizationsEdited}
            setLocalizationsNonEdited={this.setLocalizationsNonEdited}
            isShortenTextEnabled={isShortenTextEnabled}
            englishTranslationChanged={this.englishTranslationsChanged()}
            userPermissions={this.state.userPermissions && groupName !== "Group" }
          />
        </Modal>
      </Editor>
    );
  }

  // Select group specific property group
  private specificProperties = (groupName: Api.GroupName): JSX.Element => {
    if (groupName === "Drive") {
      return this.driveProperties();
    } else if (groupName === "Lift") {
      return this.liftProperties();
    } else if (groupName === "Safety") {
      return this.safetyProperties();
    } else if (groupName === "Escalator") {
      return this.escalatorProperties();
    } else if (groupName === "Door") {
      return this.doorProperties();
    } else {
      return this.groupProperties();
    }
  };

  private commonProperties = (isNew: boolean, groupName: Api.GroupName): JSX.Element => {
    const { editedParameter, pdApi } = this.props;
    const { domains, parameterCategories } = pdApi;
    const domainId = editedParameter.domain.id;
    const groupId = domains[editedParameter.domain.id].domainGroup.id;
    const domainOptions = getDomainOptionsByDomainGroup(pdApi, groupName);
    const parameterCategoryOptions = getParameterCategoryOptions(domainId, pdApi);
    const parameterCategoryList = _.values(parameterCategories).filter(
      category => category.domainGroup.id === groupId
    );
    const hasCategoryAsTextField = !isNew && groupName === "Group";
    const hasCategoryAsDropDown = !hasCategoryAsTextField;

    return (
      <>
        {this.numberField(
          "Code",
          parameterFields("code"),
          !isNew,
          false,
          "The code must be greater than zero and unique in the domain."
        )}
        {isNew ? (
          <DropdownField
            id="domainName"
            label="Domain name"
            value={editedParameter.domain.id}
            options={domainOptions}
            search={true}
            onChange={id => {
              const domain = pdApi.domains[id];
              if ("doorParameterBindings" in editedParameter) {
                const firstVersion = (pdApi.DoorVersions ?? []).find(dv => dv.domain.id === id);
                const resetBindings = (editedParameter as Api.DoorParameter).doorParameterBindings.map(
                  binding => ({
                    ...binding,
                    doorVersion: { id: firstVersion?.id ?? -1, url: firstVersion?.url ?? "" },
                    isDirty: true
                  })
                );
                this.props.editActions.setEdited({
                  ...editedParameter,
                  domain,
                  doorParameterBindings: resetBindings
                });
              } else {
                this.props.editActions.setEdited({
                  ...editedParameter,
                  domain
                });
              }
            }}
          />
        ) : (
          <TextField
            id="domainNameText"
            label="Domain name"
            readonly={true}
            value={
              pdApi.domains[domainId].enumName + " [" + pdApi.domains[domainId].enumValue + "]"
            }
            isError={false}
            onChange={() => undefined}
          />
        )}
        <br />
        {hasCategoryAsTextField && (
          <TextField
            id="category"
            label="Category"
            readonly={true}
            value={getCategoryPathForParameter(editedParameter, parameterCategoryList)}
            onChange={() => undefined}
          />
        )}
        {hasCategoryAsDropDown && (
          <DropdownField
            id="category"
            label="Category"
            value={
              editedParameter.parameterCategory == null
                ? null
                : editedParameter.parameterCategory.id
            }
            options={parameterCategoryOptions}
            onChange={id => {
              const parameterCategory = {
                id: pdApi.parameterCategories[id].id,
                url: groupName !== "Escalator" ? pdApi.parameterCategories[id].url : null
              };
              this.props.editActions.setEdited({
                ...editedParameter,
                parameterCategory
              });
            }}
            disabled={!this.state.userPermissions}
          />
        )}
        <br />
        {this.textField(
          "Sw Name",
          parameterFields("enumName"),
          groupName === "Group" ? true : !this.state.userPermissions,
          false,
          groupName === "Escalator" && !this.isEmptyString(editedParameter.enumName)
            ? "The Sw name must be unique in the domain group."
            : ""
        )}
        <Divider horizontal={true}>{"English translations"}</Divider>
        <TextIndicatorField
          id={"needsUpdateIndicator"}
          text={"Edited texts will be translated."}
          isWarning={this.englishTranslationsChanged()}
        />
        {this.textField("English name", parameterFields("name"), (!this.state.userPermissions || groupName === "Group"))}
        {this.textField("Description", parameterFields("description"), (!this.state.userPermissions || groupName === "Group"))}
        <ButtonField
          id={"viewTranslationsButton"}
          label="View translations"
          onButtonClick={this.toggleEditLocalizationStatusModal}
        />
      </>
    );
  };

  private englishTranslationsChanged = (): boolean =>
    this.props.editedParameter.name !== this.props.storedParameter?.name ||
    this.props.editedParameter.description !== this.props.storedParameter?.description;

  private handleEnglishTranslationsChange(item: Api.AnyParameter) {
    const { storedParameter, editedParameter } = this.props;
    const { locsOriginal, locsEdited } = this.state;

    // create both edited and stored item locs if needed
    const edited =
      locsEdited.length === 0
        ? [
            {
              text: item.name,
              description: item.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsParameter
          ]
        : this.state.locsEdited.map(loc => {
            if (loc.localeNavigation.locale === "en-US") {
              return {
                ...loc,
                text: item.name,
                description: item.description
              } as Api.LocsParameter;
            } else {
              return loc;
            }
          });
    const original =
      locsOriginal.length === 0
        ? [
            {
              text: storedParameter?.name,
              description: storedParameter?.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsParameter
          ]
        : locsOriginal;

    this.setState((prevState: EditParameterState) => ({
      ...prevState,
      locsEdited: edited,
      locsOriginal: original
    }));
    this.props.editActions.setEdited({
      ...editedParameter,
      name: item.name,
      description: item.description
    } as Api.AnyParameter);
  }

  private toggleEditLocalizationStatusModal = (): void => {
    this.setState((prevState: EditParameterState) => ({
      ...prevState,
      isEditLocalizationStatusOpen: !prevState.isEditLocalizationStatusOpen
    }));
  };

  private setLocalizationsEdited = (
    newLocalizations: Api.LocsParameter[],
    oldLocalizations: Api.LocsParameter[]
  ): void => {
    const { editedParameter } = this.props;
    this.setState((prevState: EditParameterState) => ({
      ...prevState,
      locsEdited: newLocalizations,
      locsOriginal: oldLocalizations
    }));
    this.props.editActions.setEdited({
      ...editedParameter,
      name:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text ||
        editedParameter?.name,
      description:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.description ||
        editedParameter?.description,
      locs: newLocalizations
    });
  };
  private setLocalizationsNonEdited = (Localizations: Api.LocsParameter[]): void => {
    this.setState((prevState: EditParameterState) => ({
      ...prevState,
      locsOriginal: Localizations
    }));
  };

  private groupProperties = (): JSX.Element => (
    <>{this.textField("Release status", groupParameterFields("releaseStatus"), true)}</>
  );

  componentDidUpdate(prevProps: { editedParameter: Api.AnyParameter }): void {
    // Only update floatValue if editedParameter is a SafetyParameter and has 'factor'
    if (
      prevProps.editedParameter !== this.props.editedParameter &&
      this.isSafetyParameter(this.props.editedParameter) &&
      this.props.editedParameter.factor !== undefined
    ) {
      this.setState({
        floatValue: this.props.editedParameter.factor
      });
    }
  }

  // Type guard for SafetyParameter
  private isSafetyParameter(param: Api.AnyParameter): param is Api.SafetyParameter {
    return (param as Api.SafetyParameter).factor !== undefined;
  }

  private safetyProperties = (): JSX.Element => {
    const { editedParameter } = this.props;
    const safetyParameter = editedParameter as Api.SafetyParameter;
    const datatypeOptions = safetyDatatypeOptions;

    const checkHexadecimal = (hex: string): boolean => {
      const hexRegex = /^0x[0-9A-Fa-f]{1,4}$/.test(hex);
      return hexRegex;
    };

    const hexToBase64 = (hexString: string) => {
      // Remove potential "0x" prefix
      if (checkHexadecimal(hexString)) {
        hexString = hexString.replace(/^0x/, "");

        // Convert hex pairs to bytes
        const hexPairs = hexString.match(/.{1,2}/g) || [];
        const bytes = new Uint8Array(hexPairs.map(byte => parseInt(byte, 16)));

        // Convert bytes to Base64 safely
        let binary = "";
        bytes.forEach(byte => (binary += String.fromCharCode(byte)));

        return btoa(binary);
      } else {
        return hexString;
      }
    };

    return (
      <>
        <div className="flex-row parameter-default-values">
          {this.numberField("Min value", "minValue", !this.state.userPermissions, false, "", true)}
          {this.numberField("Max value", "maxValue", !this.state.userPermissions)}
        </div>
        <p className="errorMessageMargin">
          {!this.isFieldValid("minValue") &&
            "The minimum value must be less than or equal to the maximum value, and vice versa."}
        </p>
        <div className="flex-row parameter-default-values">
          {this.numberField("Byte Count", "byteCount", !this.state.userPermissions)}
          {this.numberField("Byte Index", "byteIndex", !this.state.userPermissions)}
        </div>
        <div className="flex-row parameter-default-values">
          <Form.Field>
            <Form.Input
              type="number"
              step="any"
              label="Factor"
              value={
                this.state.floatValue === 0 && safetyParameter.factor === 0
                  ? this.state.floatValue
                  : safetyParameter.factor
              }
              onChange={(e, { value }) => {
                this.setState({ floatValue: parseFloat(value) });
                this.props.editActions.setEdited({
                  ...editedParameter,
                  factor: parseFloat(value)
                });
              }}
              min="0"
              disabled={!this.state.userPermissions}
            />
          </Form.Field>
        </div>
        <TextField
          id="bitMask"
          label="Bit Mask"
          value={base64ToHex(safetyParameter.bitMask as string)}
          isError={!this.state.bitMaskValid}
          disabled={!this.state.userPermissions}
          onChange={value => {
            if (checkHexadecimal(value)) {
              this.props.editActions.setEdited({
                ...editedParameter,
                bitMask: value
              });
              this.setState({ bitMaskValid: true, bitMask: hexToBase64(value) });
            } else {
              this.setState({ bitMaskValid: false });
            }
          }}
          errorMessage={
            !this.state.bitMaskValid
              ? "The bit mask must be a 16 bit hexadecimal value (e.g., 0x1A3F)."
              : ""
          }
        />
        {this.textField("Unit name", "unit", !this.state.userPermissions)}
        <DropdownField
          id="DataType"
          label="Data type"
          value={safetyParameter.dataType}
          isError={!this.isFieldValid("dataType")}
          errorMessage={
            !this.isFieldValid("dataType") ? "The data type field is required." : ""
          }
          options={datatypeOptions}
          onChange={id => {
            this.props.editActions.setEdited({
              ...editedParameter,
              dataType: id
            });
          }}
          disabled={!this.state.userPermissions}
        />
        <div className="flex-row">
          {this.checkboxField("Readonly", "readOnly", !this.state.userPermissions)}
          {this.checkboxField("Sub Title Row", "isSubTitleRow", !this.state.userPermissions)}
        </div>
      </>
    );
  };

  private liftProperties = (): JSX.Element => {
    const { editedParameter, pdApi } = this.props;
    const liftParameter = editedParameter as Api.LiftParameter;
    const liftVectorBindingOptions = getcommonEnumOptions(pdApi.enumerations, "VectorBinding");
    const resetMinMaxItemCount = (minValue: number, maxValue: number, itemCount: number) => {
      liftParameter.minValue = minValue;
      liftParameter.maxValue = maxValue;
      liftParameter.itemCount = itemCount === 0 ? 1 : itemCount;
      if (itemCount === 0) {
        //runs when ASCII is removed or added
        liftParameter.swDefaultValue = "0"; //ItemCount always 1, so last digit of string still exists, during reset. so set 0
        if (liftParameter.liftParameterValues.length > 0) {
          liftParameter.liftParameterValues = liftParameter.liftParameterValues.map(
            liftParameterValue => ({
              ...liftParameterValue,
              swDefaultValue: "0",
              minValue: minValue,
              maxValue: maxValue,
              itemCount: 1
            })
          );
        }
      }
    };

    return (
      <>
        <Permissions
          permissions={liftParameter.permissions}
          userGroups={pdApi.userGroups}
          onChanged={permissions =>
            this.props.editActions.setEdited({
              ...editedParameter,
              permissions
            })
          }
          disabled={!this.state.userPermissions}
        />
        {this.textField("Comment", liftParameterFields("comment"), !this.state.userPermissions)}
        <div className="flex-row">
          {this.checkboxField(
            "Requires reset",
            liftParameterFields("requiresReset"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Requires setup",
            liftParameterFields("requiresSetup"),
            !this.state.userPermissions
          )}
        </div>
        <LiftParameterBindings
          code={liftParameter.code}
          bindings={liftParameter.liftParameterBindings}
          kceProducts={pdApi.kceProducts}
          kceVersions={pdApi.kceVersions}
          handleBindingsChanged={liftParameterBindings =>
            this.props.editActions.setEdited({
              ...editedParameter,
              liftParameterBindings
            })
          }
          storedbindings={this.props.storedParameter as Api.LiftParameter}
          editedbindings={this.props.editedParameter as Api.LiftParameter}
          permissions={!this.state.userPermissions}
        />
        <LiftParameterValues
          id={liftParameter.id as number}
          code={liftParameter.code}
          maxValue={liftParameter.maxValue}
          minValue={liftParameter.minValue}
          itemCount={liftParameter.itemCount}
          swDefaultValues={liftParameter.swDefaultValue}
          liftParameterValues={liftParameter.liftParameterValues}
          kceProducts={pdApi.kceProducts}
          kceVersions={pdApi.kceVersions}
          editorDefines={liftParameter.editorDef}
          handleDefaultValuesChanged={(swDefaultValue, liftParameterValues, minValue, maxValue, itemCount) => {
            liftParameterValues = liftParameterValues.map(liftParameterValue => {
              liftParameterValue.kceProduct.url = null;
              liftParameterValue.kceVersion.url = null;
              return liftParameterValue;
            });
            this.props.editActions.setEdited({
              ...liftParameter,
              swDefaultValue,
              liftParameterValues,
              minValue,
              maxValue,
              itemCount: itemCount === 0 ? 1 : itemCount
            });
          }}
          permissions={!this.state.userPermissions}
        />
        <DropdownField
          id="vectorBinding"
          label="Vector binding"
          value={liftParameter.vectorBinding}
          options={liftVectorBindingOptions}
          onChange={vectorBinding => {
            this.props.editActions.setEdited({
              ...editedParameter,
              vectorBinding
            });
          }}
          disabled={!this.state.userPermissions}
        />
        <EditorDefines
          editorDef={liftParameter.editorDef}
          handleEditorDefChanged={editorDef =>
            this.props.editActions.setEdited({ ...editedParameter, editorDef })
          }
          handleMinMaxItmOnUnitChange={resetMinMaxItemCount}
          permissions={!this.state.userPermissions}
        />
      </>
    );
  };

  //Get special Value for errror handling (Drive, Escalator, Door)
  private userSelectedSpecialValue = (data: number): void => {
    this.setState({ dataFormatSpecialValue: data });
  };

  private escalatorProperties = (): JSX.Element => {
    const { editedParameter, pdApi } = this.props;
    const escalatorParameter = editedParameter as Api.EscalatorParameter;
    const decodedValue = decodeValueDataFormat(escalatorParameter.valueDataFormat);
    const decimals =
      decodedValue.range === "Number" && decodedValue.decimals ? decodedValue.decimals : 0;
    let swDefaultValue = escalatorParameter.swDefaultValue;
    if (decimals > 0) {
      swDefaultValue *= 1 / Math.pow(10, decimals);
    }
    const escalatorVectorBindingOptions = getcommonEnumOptions(
      pdApi.enumerations,
      "EscalatorVectorBinding"
    );
    const escalatorParamTypeOptions = getcommonEnumOptions(pdApi.enumerations, "E_paramType");
    const escalatorParamFormatOptions = getcommonEnumOptions(pdApi.enumerations, "E_paramFormat");
    return (
      <>
        <Permissions
          permissions={escalatorParameter.permissions}
          userGroups={pdApi.userGroups}
          onChanged={permissions =>
            this.props.editActions.setEdited({
              ...editedParameter,
              permissions
            })
          }
          disabled={!this.state.userPermissions}
        />
        {this.textField(
          "Comment",
          escalatorParameterFields("comment"),
          !this.state.userPermissions
        )}
        <div className="flex-row">
          {this.checkboxField(
            "Requires reset",
            escalatorParameterFields("requiresReset"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Requires setup",
            escalatorParameterFields("requiresSetup"),
            !this.state.userPermissions
          )}
        </div>
        <div className="flex-row">
          {this.checkboxField(
            "Requires inspection",
            escalatorParameterFields("paramInspection"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Modification in running",
            escalatorParameterFields("accessInRunning"),
            !this.state.userPermissions
          )}
        </div>
        <DropdownField
          id="paramType"
          label="Param type"
          value={escalatorParameter.paramType}
          options={escalatorParamTypeOptions}
          onChange={paramType => {
            this.props.editActions.setEdited({
              ...editedParameter,
              paramType
            });
          }}
          disabled={!this.state.userPermissions}
        />
        <DropdownField
          id="paramFormat"
          label="Param format"
          value={escalatorParameter.paramFormat}
          options={escalatorParamFormatOptions}
          onChange={paramFormat => {
            this.props.editActions.setEdited({
              ...editedParameter,
              paramFormat
            });
          }}
          disabled={!this.state.userPermissions}
        />
        <DropdownField
          id="vectorBinding"
          label="Vector binding"
          value={escalatorParameter.vectorBinding}
          options={escalatorVectorBindingOptions}
          onChange={vectorBinding => {
            this.props.editActions.setEdited({
              ...editedParameter,
              vectorBinding
            });
          }}
          disabled={!this.state.userPermissions}
        />
        <ValueDataFormat
          valueDataFormat={escalatorParameter.valueDataFormat}
          handleDataValueFormatChanged={valueDataFormat =>
            this.props.editActions.setEdited({
              ...editedParameter,
              valueDataFormat
            })
          }
          permissions={!this.state.userPermissions}
          specialValueChanged={data => this.userSelectedSpecialValue(data)}
          userspecialValue={this.state.dataFormatSpecialValue}
          preferredValueError={
            validatePreferredValueDigitsOnly(
              escalatorParameter.preferredValues ?? [],
              escalatorParameter.valueDataFormat
            )
          }
        />
        <div className="flex-row custom-row">
          {this.numberField(
            "Sw default value",
            escalatorParameterFields("swDefaultValue"),
            !this.state.userPermissions,
            false,
            "",
            true
          )}
          <TextField
            id="formattedValue"
            label="Formatted value"
            disabled={true}
            value={swDefaultValue.toFixed(decimals)}
            isError={false}
            onChange={() => undefined}
          />
        </div>
        {this.textField(
          "Unit name",
          escalatorParameterFields("unitName"),
          !this.state.userPermissions
        )}
        <PreferrerValues
          valueDataFormat={escalatorParameter.valueDataFormat}
          type={
            escalatorParameter.preferredValueTypeEnumValue
              ? escalatorParameter.preferredValueTypeEnumValue
              : PREFERRED_TYPE_ENUM_VALUES
          }
          values={escalatorParameter.preferredValues ? escalatorParameter.preferredValues : []}
          decimals={decimals}
          handleTypeChanged={preferredValueTypeEnumValue =>
            this.props.editActions.setEdited({
              ...escalatorParameter,
              preferredValueTypeEnumValue
            })
          }
          handleValuesChanged={preferredValues =>
            this.props.editActions.setEdited({
              ...escalatorParameter,
              preferredValues
            })
          }
          permissions={!this.state.userPermissions}
          validationError={
            this.getValidationError(
              escalatorParameter.preferredValues ?? [],
              escalatorParameter.preferredValueTypeEnumValue ?? PREFERRED_TYPE_ENUM_VALUES,
              escalatorParameter.valueDataFormat,
              true
            )
          }
        />
        <EscalatorParameterBindings
          domainId={escalatorParameter.domain.id}
          code={escalatorParameter.code}
          bindings={escalatorParameter.escalatorParameterBindings}
          escalatorVersions={pdApi.EscalatorVersions}
          domains={pdApi.domains}
          handleBindingsChanged={escalatorParameterBindings =>
            this.props.editActions.setEdited({
              ...escalatorParameter,
              escalatorParameterBindings
            })
          }
          permissions={!this.state.userPermissions}
          storedbindings={this.props.storedParameter as Api.EscalatorParameter}
          editedbindings={this.props.editedParameter as Api.EscalatorParameter}
        />
      </>
    );
  };

  private doorProperties = (): JSX.Element => {
    const { editedParameter, pdApi } = this.props;
    const doorParameter = editedParameter as Api.DoorParameter;
    const decodedValue = decodeValueDataFormat(doorParameter.valueDataFormat);
    const decimals =
      decodedValue.range === "Number" && decodedValue.decimals ? decodedValue.decimals : 0;
    let swDefaultValue = doorParameter.swDefaultValue;
    if (decimals > 0) {
      swDefaultValue *= 1 / Math.pow(10, decimals);
    }
    const doorVectorBindingOptions = getcommonEnumOptions(pdApi.enumerations, "DoorVectorBinding");
    return (
      <>
        <Permissions
          permissions={doorParameter.permissions}
          userGroups={pdApi.userGroups}
          onChanged={permissions =>
            this.props.editActions.setEdited({
              ...editedParameter,
              permissions
            })
          }
          disabled={!this.state.userPermissions}
        />
        {this.textField("Comment", doorParameterFields("comment"), !this.state.userPermissions)}
        <div className="flex-row">
          {this.checkboxField(
            "Requires reset",
            doorParameterFields("requiresReset"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Requires learning",
            doorParameterFields("requiresLearning"),
            !this.state.userPermissions
          )}
        </div>
        <DropdownField
          id="vectorBinding"
          label="Vector binding"
          value={doorParameter.vectorBinding}
          options={doorVectorBindingOptions}
          onChange={vectorBinding => {
            this.props.editActions.setEdited({
              ...editedParameter,
              vectorBinding
            });
          }}
          disabled={!this.state.userPermissions}
        />
        <ValueDataFormat
          valueDataFormat={doorParameter.valueDataFormat}
          handleDataValueFormatChanged={valueDataFormat =>
            this.props.editActions.setEdited({
              ...editedParameter,
              valueDataFormat
            })
          }
          permissions={!this.state.userPermissions}
          specialValueChanged={data => this.userSelectedSpecialValue(data)}
          userspecialValue={this.state.dataFormatSpecialValue}
        />
        <div className="flex-row custom-row">
          {this.numberField(
            "Sw default value",
            doorParameterFields("swDefaultValue"),
            !this.state.userPermissions
          )}
          <TextField
            id="formattedValue"
            label="Formatted value"
            disabled={true}
            value={swDefaultValue.toFixed(decimals)}
            isError={false}
            onChange={() => undefined}
          />
        </div>
        {this.textField("Unit name", doorParameterFields("unitName"), !this.state.userPermissions)}
        <DoorParameterBindings
          code={doorParameter.code}
          domainId={doorParameter.domain.id}
          handleBindingsChanged={doorParameterBindings =>
            this.props.editActions.setEdited({
              ...doorParameter,
              doorParameterBindings
            })
          }
          bindings={doorParameter.doorParameterBindings}
          permissions={!this.state.userPermissions}
        />
      </>
    );
  };

  private driveProperties = (): JSX.Element => {
    const { editedParameter, pdApi } = this.props;
    const driveParameter = editedParameter as Api.DriveParameter;
    const decodedValue = decodeValueDataFormat(driveParameter.valueDataFormat);
    const decimals =
      decodedValue.range === "Number" && decodedValue.decimals ? decodedValue.decimals : 0;
    let swDefaultValue = driveParameter.swDefaultValue;
    if (decimals > 0) {
      swDefaultValue *= 1 / Math.pow(10, decimals);
    }
    return (
      <>
        <Permissions
          permissions={driveParameter.permissions}
          userGroups={pdApi.userGroups}
          onChanged={permissions =>
            this.props.editActions.setEdited({
              ...editedParameter,
              permissions
            })
          }
          disabled={!this.state.userPermissions}
        />
        {this.numberField(
          "Store index",
          driveParameterFields("storeIndex"),
          !this.state.userPermissions
        )}
        <DropdownField
          id="visibility"
          label="Visibility"
          value={driveParameter.visibility}
          options={visibilityOptions}
          onChange={visibility => {
            this.props.editActions.setEdited({
              ...editedParameter,
              visibility
            });
          }}
          disabled={!this.state.userPermissions}
        />
        <ValueDataFormat
          valueDataFormat={driveParameter.valueDataFormat}
          handleDataValueFormatChanged={valueDataFormat =>
            this.props.editActions.setEdited({
              ...editedParameter,
              valueDataFormat
            })
          }
          permissions={!this.state.userPermissions}
          specialValueChanged={data => this.userSelectedSpecialValue(data)}
          userspecialValue={this.state.dataFormatSpecialValue}
          preferredValueError={
            validatePreferredValueDigitsOnly(
              driveParameter.preferredValues ?? [],
              driveParameter.valueDataFormat
            )
          }
        />
        <div className="flex-row custom-row">
          {this.numberField(
            "Sw default value",
            driveParameterFields("swDefaultValue"),
            !this.state.userPermissions
          )}
          <TextField
            id="formattedValue"
            label="Formatted value"
            disabled={true}
            value={swDefaultValue.toFixed(decimals)}
            isError={false}
            onChange={() => undefined}
          />
        </div>
        {this.textField("Unit name", driveParameterFields("unitName"), !this.state.userPermissions)}
        <div className="spaced-flex-row">
          {this.checkboxField(
            "Shaft setup required",
            driveParameterFields("shaftSetupRequired"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Custom motor type",
            driveParameterFields("customMotorType"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Change motor type",
            driveParameterFields("changeMotorType"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Ride comfort",
            driveParameterFields("rideComfort"),
            !this.state.userPermissions
          )}
          {this.checkboxField(
            "Localizable",
            driveParameterFields("localizable"),
            !this.state.userPermissions
          )}
        </div>
        {this.textField(
          "Trigger function",
          driveParameterFields("triggerFunction"),
          !this.state.userPermissions
        )}
        {this.numberField(
          "Extra scaling",
          driveParameterFields("extraScaling"),
          !this.state.userPermissions
        )}
        <PreferrerValues
          type={driveParameter.preferredValueTypeEnumValue}
          values={[...driveParameter.preferredValues]}
          valueDataFormat={driveParameter.valueDataFormat}
          decimals={decimals}
          handleTypeChanged={preferredValueTypeEnumValue =>
            this.props.editActions.setEdited({
              ...driveParameter,
              preferredValueTypeEnumValue
            })
          }
          handleValuesChanged={preferredValues =>
            this.props.editActions.setEdited({
              ...driveParameter,
              preferredValues
            })
          }
          permissions={!this.state.userPermissions}
          validationError={
            this.getValidationError(
              driveParameter.preferredValues,
              driveParameter.preferredValueTypeEnumValue,
              driveParameter.valueDataFormat,
              false
            )
          }
        />
        <DriveParameterBindings
          domainId={driveParameter.domain.id}
          code={driveParameter.code}
          bindings={driveParameter.driveParameterBindings}
          driveVersions={pdApi.driveVersions}
          domains={pdApi.domains}
          handleBindingsChanged={driveParameterBindings =>
            this.props.editActions.setEdited({
              ...driveParameter,
              driveParameterBindings
            })
          }
          permissions={!this.state.userPermissions}
          storedbindings={this.props.storedParameter as Api.DriveParameter}
          editedbindings={this.props.editedParameter as Api.DriveParameter}
        />
      </>
    );
  };

  private numberField = (
    label: string,
    field: string,
    readOnly?: boolean,
    disabled?: boolean,
    errorMessage?: string,
    allowNegative?: boolean
  ): JSX.Element => {
    const { editedParameter } = this.props;
    // TODO: fix types
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const numberValue: number = editedParameter[field];
    return (
      <NumberField
        id={label.replace(/\s/g, "")}
        label={label}
        value={numberValue}
        isError={!this.isFieldValid(field)}
        disabled={disabled}
        readonly={readOnly}
        onChange={value =>
          this.props.editActions.setEdited({
            ...editedParameter,
            [field]: value
          })
        }
        errorMessage={errorMessage}
        minValue={allowNegative ? "" : 0}
      />
    );
  };

  private textField = (
    label: string,
    field: string,
    readOnly = false,
    disabled = false,
    errorMessage?: string
  ): JSX.Element => {
    const { editedParameter } = this.props;
    // TODO: fix types
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const value = editedParameter[field];
    return (
      <TextField
        id={label.replace(/\s/g, "")}
        label={label}
        value={value}
        disabled={disabled}
        isError={!this.isFieldValid(field)}
        readonly={readOnly}
        onChange={changedValue =>
          this.props.editActions.setEdited({
            ...editedParameter,
            [field]: changedValue.trim()
          })
        }
        errorMessage={errorMessage}
      />
    );
  };

  private checkboxField = (label: string, field: string, disabled?: boolean): JSX.Element => {
    const { editedParameter } = this.props;
    // TODO: fix types
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const checked = editedParameter[field];
    return (
      <CheckboxField
        id={label.replace(/\s/g, "")}
        label={label}
        checked={checked}
        onChange={value =>
          this.props.editActions.setEdited({
            ...editedParameter,
            [field]: value
          })
        }
        disabled={disabled}
      />
    );
  };

  private isFieldValid = (field: string): boolean => {
    const { editedParameter } = this.props;
    switch (field) {
      case parameterFields("code"):
        return this.isCodeFieldValid();
      case parameterFields("name"): {
        const { name } = editedParameter;
        return !this.isEmptyString(name);
      }
      case parameterFields("description"): {
        const { description } = editedParameter;
        return !this.isEmptyString(description);
      }
      case parameterFields("enumName"): {
        return this.isEnumNameValid();
      }
      case liftParameterFields("liftParameterValues"): {
        const { liftParameterValues, minValue, maxValue, itemCount } = editedParameter as Api.LiftParameter;
        // Check for duplicate bindings
        if (getDuplicateLiftParameterBindings(liftParameterValues).length > 0) return false;
        // Validate each binding independently against its own min/max/itemCount
        return !liftParameterValues.some(lv => {
          const lvMin = lv.minValue ?? minValue;
          const lvMax = lv.maxValue ?? maxValue;
          const lvItm = lv.itemCount ?? itemCount;
          if (lvMin > lvMax || lvItm < 1) return true;
          return lv.swDefaultValue.split(";").some(d => Number(d) < lvMin || Number(d) > lvMax);
        });
      }
      case liftParameterFields("liftParameterBindings"): {
        const { liftParameterBindings } = editedParameter as Api.LiftParameter;
        return getDuplicateLiftParameterBindings(liftParameterBindings).length === 0;
      }
      case liftParameterFields("itemCount"): {
        const { itemCount } = editedParameter as Api.LiftParameter;
        return itemCount > 0;
      }
      case liftParameterFields("minValue"): {
        if (this.props.groupName === "Lift") {
          const {
            minValue,
            maxValue,
            swDefaultValue
          } = editedParameter as Api.LiftParameter;
          // Only check base defaults against base min/max
          const baseDefaultsBelowMin = swDefaultValue.split(";").findIndex(
            swDefault => Number(swDefault) < minValue
          );
          return minValue <= maxValue && baseDefaultsBelowMin === -1;
        } else {
          const { minValue, maxValue } = editedParameter as Api.SafetyParameter;
          return minValue <= maxValue;
        }
      }
      case liftParameterFields("maxValue"): {
        if (this.props.groupName === "Lift") {
          const {
            minValue,
            maxValue,
            swDefaultValue
          } = editedParameter as Api.LiftParameter;
          // Only check base defaults against base min/max
          const baseDefaultsAboveMax = swDefaultValue.split(";").findIndex(
            swDefault => Number(swDefault) > maxValue
          );
          return maxValue >= minValue && baseDefaultsAboveMax === -1;
        } else {
          const { minValue, maxValue } = editedParameter as Api.SafetyParameter;
          return maxValue >= minValue;
        }
      }
      case liftParameterFields("swDefaultValue"): {
        let validOrNot = -1;
        const { swDefaultValue, minValue, maxValue } = editedParameter as Api.LiftParameter;
        if (typeof swDefaultValue === "string" && minValue != undefined && maxValue != undefined) {
          const swDefaultValueArray = swDefaultValue.split(";");
          if (swDefaultValueArray.length > 0) {
            validOrNot = swDefaultValueArray.findIndex(
              defaultValue => Number(defaultValue) < minValue || Number(defaultValue) > maxValue
            );
          }
        }
        return validOrNot < 0;
      }
      case liftParameterFields("comment"): {
        const { comment } = editedParameter as Api.LiftParameter;
        return !this.isEmptyString(comment);
      }
      case driveParameterFields("driveParameterBindings"): {
        const { driveParameterBindings } = editedParameter as Api.DriveParameter;
        return getDuplicateDriveParameterBindings(driveParameterBindings).length === 0;
      }
      case doorParameterFields("doorParameterBindings"): {
        const { doorParameterBindings } = editedParameter as Api.DoorParameter;
        return getDuplicateDoorParameterBindings(doorParameterBindings).length === 0;
      }
      case driveParameterFields("valueDataFormat"): {
        const { valueDataFormat } = editedParameter as Api.DriveParameter;
        const decodedValue = decodeValueDataFormat(valueDataFormat);
        const { range } = decodedValue;
        if (range !== "Number" && this.state.dataFormatSpecialValue !== undefined) {
          return (
            this.state.dataFormatSpecialValue >= 128 && this.state.dataFormatSpecialValue <= 255
          );
        }
        return true;
      }
      
      case groupParameterFields("releaseStatus"): {
        const { releaseStatus } = editedParameter as Api.GroupParameter;
        return !this.isEmptyString(releaseStatus);
      }
      case escalatorParameterFields("preferredValues"): {
        if (this.props.groupName === "Escalator" || this.props.groupName === "Drive") {
          const isEscalator = this.props.groupName === "Escalator";
          const { preferredValues, preferredValueTypeEnumValue, valueDataFormat } =
            editedParameter as Api.EscalatorParameter | Api.DriveParameter;

          const enumValue = isEscalator
            ? (preferredValueTypeEnumValue ?? 2)
            : preferredValueTypeEnumValue;

          const validation = validatePreferredValues(
            preferredValues ?? [],
            enumValue,
            valueDataFormat,
            isEscalator
          );

          return validation.isValid;
        }
        return true;
      }
      case escalatorParameterFields("escalatorParameterBindings"): {
        const { escalatorParameterBindings, domain } = editedParameter as Api.EscalatorParameter;
        const escalatorVersions = this.props.pdApi.EscalatorVersions;
        return getDuplicateEscalatorParameterBindings(escalatorParameterBindings, escalatorVersions, domain.id).length === 0;
      }
      case escalatorParameterFields("valueDataFormat"): {
        const { valueDataFormat } = editedParameter as Api.EscalatorParameter;
        const decodedValue = decodeValueDataFormat(valueDataFormat);
        const { range } = decodedValue;
        if (range !== "Number" && this.state.dataFormatSpecialValue !== undefined) {
          return (
            this.state.dataFormatSpecialValue >= 128 && this.state.dataFormatSpecialValue <= 255
          );
        }
        return true;
      }
      case doorParameterFields("valueDataFormat"): {
        const { valueDataFormat } = editedParameter as Api.DoorParameter;
        const decodedValue = decodeValueDataFormat(valueDataFormat);
        const { range } = decodedValue;
        if (range !== "Number" && this.state.dataFormatSpecialValue !== undefined) {
          return (
            this.state.dataFormatSpecialValue >= 128 && this.state.dataFormatSpecialValue <= 255
          );
        }
        return true;
      }
      case "dataType": {
        return (
          this.props.groupName !== "Safety" ||
          Number((editedParameter as Api.SafetyParameter).dataType ?? 0) !== 0
        );
      }
      default:
        return true;
    }
  };

  private preferredValueDuplicate = (preferredValues: Array<number>): boolean => {
    const findDuplicate = (arr: Array<number>) =>
      arr.filter((item: number, index: number) => arr.indexOf(item) !== index);
    const duplicateElements = findDuplicate(preferredValues);
    return duplicateElements.length !== 0;
  };

  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedParameter } = this.props;
    const temp = _.keys(editedParameter);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };

  // Get status of edited code property. Code must be greater than zero AND
  // unique in the domain for Lift and Drive parameters OR
  // unique in the category for Group parameters
  private isCodeFieldValid = (): boolean => {
    const { editedParameter, pdApi, groupName } = this.props;
    if (editedParameter.code <= 0) return false;
    if (!Number.isInteger(editedParameter.code)) return false;

    if (groupName === "Group") {
      const { groupParameters } = pdApi;
      const editedGroupParameter: Api.GroupParameter = editedParameter as Api.GroupParameter;
      return groupParameters.find(
        parameter =>
          parameter.code === editedParameter.code &&
          parameter.id !== editedParameter.id &&
          parameter.domain.id === editedParameter.domain.id &&
          parameter.featureDbId === editedGroupParameter.featureDbId &&
          parameter.parameterCategory?.id === editedParameter.parameterCategory?.id
      )
        ? false
        : true;
    } else {
      const parameters: Api.AnyParameter[] =
        groupName === "Lift"
          ? pdApi.liftParameters || []
          : groupName === "Drive"
          ? pdApi.driveParameters || []
          : groupName === "Safety"
          ? pdApi.safetyParameters || []
          : groupName === "Escalator"
          ? pdApi.escalatorParameters || []
          : groupName === "Door"
          ? pdApi.doorParameters || []
          : [];

      return parameters.find(
        parameter =>
          parameter.code === editedParameter.code &&
          parameter.id !== editedParameter.id &&
          parameter.domain.id === editedParameter.domain.id
      )
        ? false
        : true;
    }
  };

  // Get status of edited enumName property. enumName must be unique in the domain group and not empty.
  private isEnumNameValid = (): boolean => {
    const { pdApi, editedParameter, storedParameter } = this.props;
    const { domains, domainGroups } = pdApi;
    const isNew = !storedParameter;
    const paramDomain = getGroupNameByDomainId(
      this.props.editedParameter.domain.id,
      this.props.pdApi
    );
    const editedTargetParam = editedParameter as Api.EscalatorParameter;
    const targetParams =
      paramDomain === "Escalator"
        ? (pdApi.escalatorParameters as Api.EscalatorParameter[])
            .filter(param => {
              const groupId = domains[param.domain.id]?.domainGroup.id;
              const groupName = domainGroups[groupId].enumName;
              if (groupName === "Escalator") {
                return param as Api.EscalatorParameter;
              } else {
                return null;
              }
            })
            .filter(Boolean) // Filter out null values
        : [];
    const isNotEmpty = !this.isEmptyString(editedTargetParam.enumName);
    const isUnique =
      _.values(targetParams).find(targetparam => {
        if (
          (isNew || targetparam.id !== editedTargetParam.id) &&
          (targetparam as Api.EscalatorParameter).enumName?.trim().toLowerCase() ===
            editedTargetParam.enumName?.trim().toLowerCase()
        ) {
          return targetparam;
        }
      }) === undefined; //is already available it fails condition and not unique

    return isNotEmpty && isUnique;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedParameter: state.editView.editedObject,
  storedParameter: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  isLoadingParameters: state.loadingStatus.isLoadingParameters,
  isLoadingEscalatorParameters: state.loadingStatus.isLoadingEscalatorParameters,
  isShortenTextEnabled: state.main.isShortenTextEnabled
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditParameter);
